import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Keyboard, Platform} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {useDispatch} from 'react-redux';

import {
  setWebAfterOverride,
  setWebAvatarOverride,
  setWebBeforeOverride,
} from '/redux/action/coach.action';
import {
  showProfileSaveErrorToast,
  showProfileSaveSuccessToast,
  showUploadErrorToast,
  showUploadSuccessToast,
} from '/util/toast.util';

import {PickedImage} from '../hooks/useImagePicker';
import {
  ABOUT_MAX_LENGTH,
  DEFAULT_COUNTRY,
  normalizeCountry,
} from '../ManageMySite.constants';
import {ManageMySiteEditProps} from '../ManageMySite.types';
import {
  formatUSPhone,
  stripPhoneToDigits,
  validateManageMySiteForm,
} from '../util/manageMySite.util';

import {
  AvatarStatus,
  PhotoSaveResult,
  TouchedField,
  UploadResponse,
} from './ManageMySiteEditScreen.types';

const resolvePhotoUrl = (hasImage: boolean, url?: string): string => {
  const normalizedUrl = url || '';

  if (hasImage) {
    return normalizedUrl;
  }

  return normalizedUrl || '';
};

const FIELD_ERROR_MAP: Record<string, string> = {
  webAlias: 'slug',
  email: 'mail',
  phonePrimary: 'primary',
  phoneSecondary: 'secondary',
  zipcode: 'zip',
};

const EMPTY_PHOTO_SAVE_RESULT: PhotoSaveResult = {
  avatarSuccess: false,
  beforeSuccess: false,
  afterSuccess: false,
  avatarError: false,
  beforeError: false,
  afterError: false,
};

export const useManageMySiteEdit = ({
  onSave,
  onPhotoUpload,
  avatarUrl = '',
  webAlias = '',
  baseWebsiteUrl = '',
  displayName = '',
  email = '',
  phonePrimary = '',
  phoneSecondary = '',
  street = '',
  city = '',
  state = '',
  zipcode = '',
  about = '',
  country = '',
  transformationBeforeUrl = '',
  transformationAfterUrl = '',
  hasBeforeImage = false,
  hasAfterImage = false,
  onClose,
}: ManageMySiteEditProps) => {
  const dispatch = useDispatch();
  const scrollRef = useRef<KeyboardAwareScrollView>(null);

  const isUS = (normalizeCountry(country) || 'US') === 'US';

  const [slug, setSlug] = useState(webAlias);
  const [name, setName] = useState(displayName);
  const [mail, setMail] = useState(email);

  const [primary, setPrimary] = useState(
    isUS ? formatUSPhone(phonePrimary) : phonePrimary,
  );

  const [secondary, setSecondary] = useState(
    isUS ? formatUSPhone(phoneSecondary) : phoneSecondary,
  );

  const [streetVal, setStreetVal] = useState(street);
  const [cityVal, setCityVal] = useState(city);
  const [countryVal, setCountryVal] = useState(country);
  const [stateVal, setStateVal] = useState(state);
  const [zip, setZip] = useState(zipcode);
  const [aboutVal, setAboutVal] = useState(about);

  const [avatar, setAvatar] = useState(avatarUrl);
  const [before, setBefore] = useState(
    resolvePhotoUrl(hasBeforeImage, transformationBeforeUrl),
  );
  const [after, setAfter] = useState(
    resolvePhotoUrl(hasAfterImage, transformationAfterUrl),
  );

  const [avatarPreview, setAvatarPreview] = useState<PickedImage | undefined>();

  const [avatarRetryImage, setAvatarRetryImage] = useState<
    PickedImage | undefined
  >();
  const [beforeRetryImage, setBeforeRetryImage] = useState<
    PickedImage | undefined
  >();
  const [afterRetryImage, setAfterRetryImage] = useState<
    PickedImage | undefined
  >();

  const [avatarStatus, setAvatarStatus] = useState<AvatarStatus>(
    avatarUrl ? 'uploaded' : 'empty',
  );

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [touchedFields, setTouchedFields] = useState<
    Record<TouchedField, boolean>
  >({
    slug: false,
    name: false,
    mail: false,
    primary: false,
    secondary: false,
    zip: false,
    about: false,
  });

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const [serverValidationErrors, setServerValidationErrors] = useState<
    Record<string, string>
  >({});

  const [photoUploadState, setPhotoUploadState] = useState({
    avatar: false,
    before: false,
    after: false,
  });

  const [photoSaveResult, setPhotoSaveResult] = useState<PhotoSaveResult>(
    EMPTY_PHOTO_SAVE_RESULT,
  );

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, e => {
      setKeyboardHeight(e.endCoordinates.height);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  useEffect(() => {
    if (
      !photoUploadState.avatar &&
      avatarStatus !== 'error' &&
      avatarStatus !== 'preview'
    ) {
      setAvatar(avatarUrl || '');
      setAvatarStatus(avatarUrl ? 'uploaded' : 'empty');
    }
  }, [avatarUrl, photoUploadState.avatar, avatarStatus]);

  useEffect(() => {
    if (!photoUploadState.before) {
      setBefore(resolvePhotoUrl(hasBeforeImage, transformationBeforeUrl));
    }
  }, [hasBeforeImage, transformationBeforeUrl, photoUploadState.before]);

  useEffect(() => {
    if (!photoUploadState.after) {
      setAfter(resolvePhotoUrl(hasAfterImage, transformationAfterUrl));
    }
  }, [hasAfterImage, transformationAfterUrl, photoUploadState.after]);

  const handleSlugChange = useCallback((value: string) => {
    setSlug(value);

    setServerValidationErrors(prev => {
      const updated = {...prev};
      delete updated.slug;
      return updated;
    });
  }, []);

  const isOverAboutLimit = aboutVal.length > ABOUT_MAX_LENGTH;

  const resolvedBaseWebsiteUrl = useMemo(() => {
    const cleanBase = (baseWebsiteUrl || '').trim();

    const fallback =
      process.env.ENV === 'prod'
        ? 'https://coach.optavia.com/'
        : 'https://coach.qa5-optavia.com';

    return (cleanBase || fallback).replace(/\/+$/, '');
  }, [baseWebsiteUrl]);

  const hasProfileChanges = useMemo(() => {
    const isPhoneChanged = (stateVal: string, propVal: string) =>
      stripPhoneToDigits(stateVal) !== stripPhoneToDigits(propVal);

    return (
      slug !== webAlias ||
      name !== displayName ||
      mail !== email ||
      isPhoneChanged(primary, phonePrimary) ||
      isPhoneChanged(secondary, phoneSecondary ?? '') ||
      streetVal !== street ||
      cityVal !== city ||
      stateVal !== state ||
      stripPhoneToDigits(zip) !== stripPhoneToDigits(zipcode) ||
      aboutVal !== about ||
      normalizeCountry(countryVal) !== normalizeCountry(country)
    );
  }, [
    slug,
    webAlias,
    name,
    displayName,
    mail,
    email,
    primary,
    phonePrimary,
    secondary,
    phoneSecondary,
    streetVal,
    street,
    cityVal,
    city,
    stateVal,
    state,
    zip,
    zipcode,
    aboutVal,
    about,
    countryVal,
    country,
  ]);

  /**
   * Sticky Save should only care about profile fields.
   * Media upload is independent and should not enable Save.
   */
  const formDirty = hasProfileChanges;

  const isAnyPhotoUploading = useMemo(
    () =>
      photoUploadState.avatar ||
      photoUploadState.before ||
      photoUploadState.after,
    [photoUploadState],
  );

  const unsavedChanges = useMemo(
    () => hasProfileChanges || !!avatarPreview,
    [hasProfileChanges, avatarPreview],
  );

  const validationErrors = useMemo(() => {
    const clientErrors = validateManageMySiteForm({
      slug,
      name,
      mail,
      primary,
      secondary,
      zip,
      aboutVal,
      countryVal,
    });

    return {
      ...clientErrors,
      ...serverValidationErrors,
    };
  }, [
    slug,
    name,
    mail,
    primary,
    secondary,
    zip,
    aboutVal,
    countryVal,
    serverValidationErrors,
  ]);

  const {errorCount, hasErrors, hasTouchedError} = useMemo(() => {
    const keys = Object.keys(validationErrors) as TouchedField[];

    const activeErrors = keys.filter(key => !!validationErrors[key]);

    const touchedErrors = keys.filter(
      key => !!validationErrors[key] && !!touchedFields[key],
    );

    const count = hasSubmitted ? activeErrors.length : touchedErrors.length;

    return {
      errorCount: count,
      hasErrors: activeErrors.length > 0,
      hasTouchedError: touchedErrors.length > 0,
    };
  }, [validationErrors, touchedFields, hasSubmitted]);

  const uploadPhotoImmediately = useCallback(
    async (type: 'avatar' | 'before' | 'after', image: PickedImage) => {
      setPhotoUploadState(prev => ({
        ...prev,
        [type]: true,
      }));

      setPhotoSaveResult(prev => ({
        ...prev,
        [`${type}Success`]: false,
        [`${type}Error`]: false,
      }));

      if (type === 'avatar') {
        setAvatarStatus('uploading');
      }

      let uploadSuccess = false;

      try {
        const response = (await onPhotoUpload(
          type,
          image,
        )) as UploadResponse | void;

        const uploadedUrl = response?.imageUrl || image.uri;
        uploadSuccess = true;

        if (type === 'avatar') {
          setAvatar(uploadedUrl);
          setAvatarPreview(undefined);
          setAvatarRetryImage(undefined);

          if (Platform.OS === 'web') {
            dispatch(setWebAvatarOverride(image.uri));
          }
        }

        if (type === 'before') {
          setBefore(uploadedUrl);
          setBeforeRetryImage(undefined);

          if (Platform.OS === 'web') {
            dispatch(setWebBeforeOverride(image.uri));
          }
        }

        if (type === 'after') {
          setAfter(uploadedUrl);
          setAfterRetryImage(undefined);

          if (Platform.OS === 'web') {
            dispatch(setWebAfterOverride(image.uri));
          }
        }

        setPhotoSaveResult(prev => ({
          ...prev,
          avatarSuccess: type === 'avatar' ? true : prev.avatarSuccess,
          beforeSuccess: type === 'before' ? true : prev.beforeSuccess,
          afterSuccess: type === 'after' ? true : prev.afterSuccess,
          avatarError: type === 'avatar' ? false : prev.avatarError,
          beforeError: type === 'before' ? false : prev.beforeError,
          afterError: type === 'after' ? false : prev.afterError,
        }));

        showUploadSuccessToast(type);

        return 'success' as const;
      } catch {
        if (type === 'avatar') {
          setAvatar(image.uri);
          setAvatarStatus('error');
          setAvatarRetryImage(image);
        }

        if (type === 'before') {
          setBefore(image.uri);
          setBeforeRetryImage(image);
        }

        if (type === 'after') {
          setAfter(image.uri);
          setAfterRetryImage(image);
        }

        setPhotoSaveResult(prev => ({
          ...prev,
          avatarError: type === 'avatar' ? true : prev.avatarError,
          beforeError: type === 'before' ? true : prev.beforeError,
          afterError: type === 'after' ? true : prev.afterError,
          avatarSuccess: type === 'avatar' ? false : prev.avatarSuccess,
          beforeSuccess: type === 'before' ? false : prev.beforeSuccess,
          afterSuccess: type === 'after' ? false : prev.afterSuccess,
        }));

        showUploadErrorToast(type);

        return 'error' as const;
      } finally {
        if (type === 'avatar' && uploadSuccess) {
          setAvatarStatus('uploaded');
        }

        setPhotoUploadState(prev => ({
          ...prev,
          [type]: false,
        }));
      }
    },
    [onPhotoUpload, dispatch],
  );

  const handleSave = useCallback(async () => {
    setHasSubmitted(true);
    setServerValidationErrors({});

    const currentErrors = validateManageMySiteForm({
      slug,
      name,
      mail,
      primary,
      secondary,
      zip,
      aboutVal,
      countryVal,
    });

    if (Object.keys(currentErrors).length > 0) {
      scrollRef.current?.scrollToPosition?.(0, 0, true);
      return;
    }

    try {
      if (hasProfileChanges) {
        await onSave({
          webAlias: slug.trim(),
          displayName: name.trim(),
          email: mail.trim(),
          phonePrimary: primary,
          phoneSecondary: secondary ? secondary : '',
          country: DEFAULT_COUNTRY,
          street: streetVal.trim(),
          city: cityVal.trim(),
          state: stateVal,
          zipcode: zip.trim(),
          about: aboutVal,
        });

        showProfileSaveSuccessToast();
      }

      onClose?.();
    } catch (error: any) {
      const fieldKey = FIELD_ERROR_MAP[error?.field];

      if (fieldKey) {
        setServerValidationErrors(prev => ({
          ...prev,
          [fieldKey]: error.message,
        }));

        showProfileSaveErrorToast();
        scrollRef.current?.scrollToPosition?.(0, 0, true);
        return;
      }

      showProfileSaveErrorToast();
      throw error;
    }
  }, [
    hasProfileChanges,
    onSave,
    onClose,
    slug,
    name,
    mail,
    primary,
    secondary,
    streetVal,
    cityVal,
    stateVal,
    zip,
    aboutVal,
    countryVal,
  ]);

  const resetUnsavedState = useCallback(() => {
    setSlug(webAlias);
    setName(displayName);
    setMail(email);
    setPrimary(isUS ? formatUSPhone(phonePrimary) : phonePrimary);
    setSecondary(isUS ? formatUSPhone(phoneSecondary) : phoneSecondary ?? '');
    setStreetVal(street);
    setCityVal(city);
    setStateVal(state);
    setZip(zipcode);
    setAboutVal(about);
    setCountryVal(country);

    setAvatar(avatarUrl || '');
    setAvatarPreview(undefined);
    setAvatarRetryImage(undefined);

    setBefore(resolvePhotoUrl(hasBeforeImage, transformationBeforeUrl));
    setAfter(resolvePhotoUrl(hasAfterImage, transformationAfterUrl));
    setBeforeRetryImage(undefined);
    setAfterRetryImage(undefined);

    setHasSubmitted(false);
    setPhotoSaveResult(EMPTY_PHOTO_SAVE_RESULT);

    setTouchedFields({
      slug: false,
      name: false,
      mail: false,
      primary: false,
      secondary: false,
      zip: false,
      about: false,
    });

    setServerValidationErrors({});
    setAvatarStatus(avatarUrl ? 'uploaded' : 'empty');
  }, [
    webAlias,
    displayName,
    email,
    phonePrimary,
    phoneSecondary,
    street,
    city,
    state,
    zipcode,
    about,
    country,
    avatarUrl,
    hasBeforeImage,
    hasAfterImage,
    transformationBeforeUrl,
    transformationAfterUrl,
    isUS,
  ]);

  const handleAvatarPicked = useCallback((image: PickedImage) => {
    setAvatarPreview(image);
    setAvatarStatus('preview');

    setPhotoSaveResult(prev => ({
      ...prev,
      avatarSuccess: false,
      avatarError: false,
    }));
  }, []);

  const handleAvatarConfirm = useCallback(async () => {
    if (!avatarPreview?.uri) {
      return;
    }

    const result = await uploadPhotoImmediately('avatar', avatarPreview);

    if (result === 'error') {
      throw new Error('avatar upload failed');
    }
  }, [avatarPreview, uploadPhotoImmediately]);

  const handleAvatarCancel = useCallback(() => {
    const originalAvatar = avatarUrl || '';

    setAvatarPreview(undefined);
    setAvatarRetryImage(undefined);
    setAvatar(originalAvatar);
    setAvatarStatus(originalAvatar ? 'uploaded' : 'empty');

    setPhotoSaveResult(prev => ({
      ...prev,
      avatarSuccess: false,
      avatarError: false,
    }));
  }, [avatarUrl]);

  const handlePhotoUpload = useCallback(
    async (type: 'before' | 'after', image: PickedImage) => {
      if (type === 'before') {
        setBefore(image.uri);
        setBeforeRetryImage(image);

        setPhotoSaveResult(prev => ({
          ...prev,
          beforeSuccess: false,
          beforeError: false,
        }));
      } else {
        setAfter(image.uri);
        setAfterRetryImage(image);

        setPhotoSaveResult(prev => ({
          ...prev,
          afterSuccess: false,
          afterError: false,
        }));
      }

      const result = await uploadPhotoImmediately(type, image);

      if (result === 'error') {
        throw new Error(`${type} photo upload failed`);
      }
    },
    [uploadPhotoImmediately],
  );

  const handleRetryAvatarUpload = useCallback(async () => {
    if (!avatarRetryImage) {
      return;
    }

    const result = await uploadPhotoImmediately('avatar', avatarRetryImage);

    if (result === 'error') {
      throw new Error('avatar retry failed');
    }
  }, [avatarRetryImage, uploadPhotoImmediately]);

  const handleRetryBeforeUpload = useCallback(async () => {
    if (!beforeRetryImage) {
      return;
    }

    await uploadPhotoImmediately('before', beforeRetryImage);
  }, [beforeRetryImage, uploadPhotoImmediately]);

  const handleRetryAfterUpload = useCallback(async () => {
    if (!afterRetryImage) {
      return;
    }

    await uploadPhotoImmediately('after', afterRetryImage);
  }, [afterRetryImage, uploadPhotoImmediately]);

  const handleTransformationPhotoCancel = useCallback(
    (type: 'before' | 'after') => {
      if (type === 'before') {
        setBefore(resolvePhotoUrl(hasBeforeImage, transformationBeforeUrl));
        setBeforeRetryImage(undefined);

        setPhotoSaveResult(prev => ({
          ...prev,
          beforeSuccess: false,
          beforeError: false,
        }));
      } else {
        setAfter(resolvePhotoUrl(hasAfterImage, transformationAfterUrl));
        setAfterRetryImage(undefined);

        setPhotoSaveResult(prev => ({
          ...prev,
          afterSuccess: false,
          afterError: false,
        }));
      }
    },
    [
      hasBeforeImage,
      hasAfterImage,
      transformationBeforeUrl,
      transformationAfterUrl,
    ],
  );

  const markFieldTouched = useCallback((field: TouchedField) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  return {
    scrollRef,

    values: {
      slug,
      name,
      mail,
      primary,
      secondary,
      streetVal,
      cityVal,
      stateVal,
      country,
      countryVal,
      zip,
      aboutVal,
      avatar,
      before,
      after,
      avatarPreview,
      keyboardHeight,
    },

    setters: {
      setSlug: handleSlugChange,
      setName,
      setMail,
      setPrimary,
      setSecondary,
      setStreetVal,
      setCityVal,
      setStateVal,
      setCountryVal,
      setZip,
      setAboutVal,
      setAvatarPreview,
      setHasPendingPhotoPreview: () => {},
      markFieldTouched,
    },

    derived: {
      hasSubmitted,
      hasErrors,
      errorCount,
      validationErrors,
      touchedFields,
      formDirty,
      unsavedChanges,
      isOverAboutLimit,
      resolvedBaseWebsiteUrl,
      isAnyPhotoUploading,
      isAvatarUploading: photoUploadState.avatar,
      avatarStatus,
      hasTouchedError,

      avatarUploadSuccess: photoSaveResult.avatarSuccess,
      beforeUploadSuccess: photoSaveResult.beforeSuccess,
      afterUploadSuccess: photoSaveResult.afterSuccess,

      avatarUploadError: photoSaveResult.avatarError,
      beforeUploadError: photoSaveResult.beforeError,
      afterUploadError: photoSaveResult.afterError,
    },

    actions: {
      handleSave,
      handleAvatarPicked,
      handleAvatarConfirm,
      handleAvatarCancel,
      handlePhotoUpload,
      handleRetryBeforeUpload,
      handleRetryAfterUpload,
      resetUnsavedState,
      handleTransformationPhotoCancel,
      handleRetryAvatarUpload,
    },
  };
};
